import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { closeBuyBonus } from '@slices/uiSlice'
import { useBuyBonusPackages, useBuyBonusPackage } from '@hooks/queries/useBuyBonus'
import { BUY_BONUS_OVERLAY, BUY_BONUS_PAYMENT_STATUS } from '@constants/buyBonusConstants'
import { filterActivePackages } from '@core/buyBonus/buyBonusView'

// Owns the entire Buy Bonus purchase flow — which of the 3 screens is
// showing, the selected package, the packages query, the purchase mutation.
// BuyBonusOverlay and its 3 pure screens only render what this returns; none
// of them hold state or call a hook themselves. Mirrors the Controller/
// pure-render split used for Tournaments/Jackpot.
export const useBuyBonusFlow = () => {
  const dispatch = useDispatch()
  const isOpen = useSelector((state) => state.ui.buyBonus.open)

  const [currentOverlay, setCurrentOverlay] = useState(BUY_BONUS_OVERLAY.PACKAGES_LIST)
  const [selectedPackage, setSelectedPackage] = useState(null)
  const [paymentStatus, setPaymentStatus] = useState(null)

  // Reset to a fresh list only when the overlay OPENS (not on every internal
  // screen swap) — the "adjust state during render" pattern
  // (BettingPanel.jsx's requestedFreebetId/seenRequest uses the same shape).
  // This used to be wired to AnimatePresence's onExitComplete instead, which
  // fires on every screen transition, not just when the overlay itself
  // closes — so pressing "Buy" immediately snapped back to the list right
  // after switching to checkout.
  const [seenOpen, setSeenOpen] = useState(isOpen)
  if (isOpen !== seenOpen) {
    setSeenOpen(isOpen)
    if (isOpen) {
      setCurrentOverlay(BUY_BONUS_OVERLAY.PACKAGES_LIST)
      setSelectedPackage(null)
      setPaymentStatus(null)
    }
  }

  const { data, isLoading } = useBuyBonusPackages({}, { enabled: isOpen })
  const packages = filterActivePackages(data?.rows)
  const isEmpty = !isLoading && packages.length === 0

  const { mutate: purchase, isPending: isPurchasing } = useBuyBonusPackage({
    onSuccess: () => {
      setPaymentStatus(BUY_BONUS_PAYMENT_STATUS.SUCCESS)
      setCurrentOverlay(BUY_BONUS_OVERLAY.PACKAGE_PAYMENT_STATUS)
    },
    onError: () => {
      setPaymentStatus(BUY_BONUS_PAYMENT_STATUS.FAILURE)
      setCurrentOverlay(BUY_BONUS_OVERLAY.PACKAGE_PAYMENT_STATUS)
    },
  })

  const onSelectPackage = (pkg) => {
    setSelectedPackage((current) => (current?.id === pkg.id ? null : pkg))
  }

  const onProceedToCheckout = () => setCurrentOverlay(BUY_BONUS_OVERLAY.PACKAGE_CHECKOUT)
  const onBackToList = () => setCurrentOverlay(BUY_BONUS_OVERLAY.PACKAGES_LIST)

  const onPurchase = () => {
    if (!selectedPackage || isPurchasing) return
    purchase({ packageValue: selectedPackage.packageValue, packageId: selectedPackage.id })
  }

  const onClose = () => dispatch(closeBuyBonus())

  // "Play"/"Buy" button on the terminal status screen — success closes the
  // overlay (the player goes play their new freebets), failure returns to the
  // list so they can retry. Ports defenderV2's handleFinalAction branch.
  const onFinalAction = () => {
    if (paymentStatus === BUY_BONUS_PAYMENT_STATUS.SUCCESS) {
      dispatch(closeBuyBonus())
      return
    }
    setCurrentOverlay(BUY_BONUS_OVERLAY.PACKAGES_LIST)
    setSelectedPackage(null)
    setPaymentStatus(null)
  }

  return {
    isOpen,
    currentOverlay,
    packages,
    isLoading,
    isEmpty,
    selectedPackage,
    isPurchasing,
    paymentStatus,
    onSelectPackage,
    onProceedToCheckout,
    onBackToList,
    onPurchase,
    onClose,
    onFinalAction,
  }
}
